/**
 * Result card — shown after the win banner. Carries the share grid + buttons.
 *
 * Render order (PRD-LEADERBOARD §9):
 *   Win banner → Today's Office strip → Result stats → Share grid →
 *   "Share my grid" → "Copy result" → "Copy with rank" (conditional) →
 *   "Challenge a colleague".
 *
 * The strip + "Copy with rank" appear only when /api/score-submit returned
 * accepted=true with samples ≥ 10. All graceful-degradation paths simply
 * hide the strip and the rank button — the existing share flow is preserved.
 */

import { buildShareText, buildShareTextWithRank, shareOrCopy, buildEmailShare } from '@/game/share';
import type { GameState } from '@/types/game';
import { firstTryCount, elapsedSeconds } from '@/game/engine';
import { trackEvent } from '@/analytics/gtag';
import {
  computeCompletionHash,
  fetchStartToken,
  isLeaderboardEnabled,
  submitScore,
} from '@/game/leaderboard';
import { createTodayStrip } from '@/ui/today-strip';
import { createSubmitForm } from '@/ui/submit-form';
import type { ScoreSubmitResponse } from '@/types/leaderboard';

export interface ResultCardOpts {
  state: GameState;
  numericId: string;
  streak: number;
  onShareCopied?: () => void;
}

export function renderResultCard(opts: ResultCardOpts): HTMLElement {
  const { state, numericId, streak } = opts;
  const root = document.createElement('section');
  root.className = 'result-card';
  root.setAttribute('aria-label', 'Puzzle result');

  const seconds = elapsedSeconds(state);
  const ftc = firstTryCount(state);
  const shareText = buildShareText({ state, numericId, streak });

  const stats = document.createElement('div');
  stats.className = 'result-stats';
  stats.innerHTML = `
    <span>Time<span class="result-stat-value">${formatTime(seconds)}</span></span>
    <span>First try<span class="result-stat-value">${ftc}/4</span></span>
    <span>Streak<span class="result-stat-value">${streak}</span></span>
  `;

  const grid = document.createElement('pre');
  grid.className = 'share-grid-block';
  grid.setAttribute('aria-label', 'Share grid preview');
  grid.textContent = shareText;

  const btnGroup = document.createElement('div');
  btnGroup.className = 'btn-group';

  const shareBtn = document.createElement('button');
  shareBtn.className = 'btn btn-primary';
  shareBtn.type = 'button';
  shareBtn.textContent = 'Share my grid';

  const copyBtn = document.createElement('button');
  copyBtn.className = 'btn btn-secondary';
  copyBtn.type = 'button';
  copyBtn.textContent = 'Copy result';

  const emailBtn = document.createElement('a');
  emailBtn.className = 'btn btn-secondary';
  emailBtn.setAttribute('role', 'button');
  emailBtn.textContent = 'Challenge a colleague';
  const email = buildEmailShare(shareText, ftc);
  emailBtn.href = email.mailto;
  emailBtn.addEventListener('click', () => {
    trackEvent({
      name: 'email_share_clicked',
      params: { puzzle_id: state.puzzleId, first_try_accuracy: ftc / 4 },
    });
  });

  const confirm = document.createElement('p');
  confirm.className = 'copy-confirm';
  confirm.setAttribute('aria-live', 'polite');

  shareBtn.addEventListener('click', async () => {
    const out = await shareOrCopy(shareText);
    trackEvent({ name: 'share_clicked', params: { puzzle_id: state.puzzleId, method: out.method } });
    if (out.method === 'clipboard' && out.ok) {
      trackEvent({ name: 'share_copied', params: { puzzle_id: state.puzzleId } });
      confirm.textContent = 'Copied. Paste anywhere.';
      opts.onShareCopied?.();
    } else if (!out.ok) {
      confirm.textContent = 'Copy failed. Select the grid and copy manually.';
    } else {
      confirm.textContent = 'Shared.';
    }
  });

  copyBtn.addEventListener('click', async () => {
    const out = await shareOrCopy(shareText);
    if (out.ok) {
      trackEvent({ name: 'share_copied', params: { puzzle_id: state.puzzleId } });
      confirm.textContent = 'Copied. Paste anywhere.';
      opts.onShareCopied?.();
    } else {
      confirm.textContent = 'Copy failed. Select the grid and copy manually.';
    }
  });

  // ── Today's Office strip — wired only if leaderboard enabled ─────────────
  // The strip is appended ABOVE the result stats per PRD §9 wiring.
  let strip: ReturnType<typeof createTodayStrip> | null = null;
  if (isLeaderboardEnabled()) {
    strip = createTodayStrip({
      numericId,
      playerSeconds: seconds,
    });
    // Loading state by default — caller appends, async submit updates state.
    strip.setState('loading');
  }

  // ── "Copy with rank" — created up-front, hidden until response arrives ──
  // Per PRD §7 + §9: a SECOND option, never replacing the original copy flow.
  const copyRankBtn = document.createElement('button');
  copyRankBtn.className = 'btn btn-secondary btn-rank';
  copyRankBtn.type = 'button';
  copyRankBtn.textContent = 'Copy with rank';
  copyRankBtn.hidden = true;
  copyRankBtn.setAttribute('data-leaderboard-rank-btn', 'true');

  btnGroup.appendChild(shareBtn);
  btnGroup.appendChild(copyBtn);
  btnGroup.appendChild(copyRankBtn);
  btnGroup.appendChild(emailBtn);

  // ── Submit form — appears below the share buttons; opt-in named entry. ──
  let submitForm: ReturnType<typeof createSubmitForm> | null = null;

  if (strip) root.appendChild(strip.element);
  root.appendChild(stats);
  root.appendChild(grid);
  root.appendChild(btnGroup);
  root.appendChild(confirm);

  // ── Async leaderboard submit — non-blocking, fully graceful ─────────────
  if (isLeaderboardEnabled() && strip) {
    submitForm = createSubmitForm({
      onSubmit: async ({ nickname, country }) => {
        const token = await fetchStartToken(state.puzzleId);
        if (!token) {
          submitForm?.showError('Leaderboard unavailable. Try again later.');
          return;
        }
        const completion_hash = await computeCompletionHash(
          state.puzzleId,
          seconds,
          ftc,
          token,
        );
        const response = await submitScore({
          puzzle_id: state.puzzleId,
          seconds,
          first_try_count: ftc,
          completion_hash,
          token,
          nickname,
          country,
        });
        if (!response) {
          submitForm?.showError('Network hiccup. Try again in a sec.');
          return;
        }
        if (!response.accepted) {
          const reasonCopy: Record<string, string> = {
            rate_limited: 'You have already claimed this puzzle. Come back tomorrow.',
            token_invalid: 'Session expired. Refresh the page and try again.',
            token_expired: 'Session expired. Refresh the page and try again.',
            token_replay: 'Refresh the page and try again.',
            hash_invalid: 'Score check failed. Refresh and try again.',
            rejected_floor: 'That time looks too fast to record.',
            disabled: 'Leaderboard is paused right now. Try later.',
          };
          submitForm?.showError(
            reasonCopy[response.reason ?? ''] ?? 'Could not add you to the board. Try again.',
          );
          return;
        }
        if (response.reason === 'invalid_nickname') {
          submitForm?.showError('That nickname is not allowed. Try another.');
          return;
        }
        if (response.reason === 'invalid_country') {
          submitForm?.showError('That country is not on the list.');
          return;
        }
        // Re-render strip with the named-board response.
        strip!.setState('ready', response);
        submitForm?.lock();
      },
    });
    root.appendChild(submitForm.element);

    void runLeaderboardSubmit({
      puzzleId: state.puzzleId,
      seconds,
      firstTry: ftc,
      strip,
      copyRankBtn,
      shareTextBuilder: (resp) =>
        buildShareTextWithRank({
          state,
          numericId,
          streak,
          percentile: resp.percentile ?? 0,
          pacePar: 19,
        }),
      onCopied: () => {
        trackEvent({ name: 'share_copied', params: { puzzle_id: state.puzzleId } });
        confirm.textContent = 'Copied. Paste anywhere.';
        opts.onShareCopied?.();
      },
      onCopyFailed: () => {
        confirm.textContent = 'Copy failed. Select the grid and copy manually.';
      },
    });
  }

  return root;
}

interface LeaderboardSubmitOpts {
  puzzleId: string;
  seconds: number;
  firstTry: number;
  strip: ReturnType<typeof createTodayStrip>;
  copyRankBtn: HTMLButtonElement;
  shareTextBuilder: (resp: ScoreSubmitResponse) => string;
  onCopied: () => void;
  onCopyFailed: () => void;
}

/**
 * Run the leaderboard fetch chain. Any failure → strip hidden, rank button
 * stays hidden. Never throws. Never affects the existing share flow.
 *
 * Order:
 *   1. Fetch start token (timeout 3s).
 *   2. Compute completion_hash from token + puzzle data.
 *   3. Submit score (timeout 4s).
 *   4. On accepted + samples ≥ 10: strip → 'ready', show "Copy with rank".
 *   5. On accepted + samples < 10: strip → 'empty', no rank button.
 *   6. On any failure / rejected: strip → 'hidden', no rank button.
 */
async function runLeaderboardSubmit(opts: LeaderboardSubmitOpts): Promise<void> {
  try {
    const token = await fetchStartToken(opts.puzzleId);
    if (!token) {
      opts.strip.setState('hidden');
      return;
    }
    const completion_hash = await computeCompletionHash(
      opts.puzzleId,
      opts.seconds,
      opts.firstTry,
      token,
    );
    const response = await submitScore({
      puzzle_id: opts.puzzleId,
      seconds: opts.seconds,
      first_try_count: opts.firstTry,
      completion_hash,
      token,
    });
    if (!response || !response.accepted) {
      opts.strip.setState('hidden');
      return;
    }
    opts.strip.setState('ready', response);
    if (response.percentile !== null && response.samples >= 10) {
      // Bind the rank button click — copies the share-with-rank text.
      const rankText = opts.shareTextBuilder(response);
      opts.copyRankBtn.hidden = false;
      opts.copyRankBtn.addEventListener('click', async () => {
        const out = await shareOrCopy(rankText);
        if (out.ok) opts.onCopied();
        else opts.onCopyFailed();
      });
    }
  } catch {
    // Defensive — should not throw, but never break the win flow.
    opts.strip.setState('hidden');
  }
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
